<template>
  <div class="flex flex-col">
    <h2>Clientes</h2>

    <div class="mt-2 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
      <custom-search
        class="w-full sm:w-[400px]"
        placeholder="Buscar por nombre o email"
        v-model="searchText"
        @confirm="search"
        @clear="search"
      />

      <div class="flex items-end gap-2">
        <div class="flex flex-col">
          <label class="text-xs text-content-400 mb-1">Registro desde</label>
          <input type="date" v-model="createdFrom" class="border px-3 py-2 rounded" />
        </div>
        <div class="flex flex-col">
          <label class="text-xs text-content-400 mb-1">Registro hasta</label>
          <input type="date" v-model="createdTo" class="border px-3 py-2 rounded" />
        </div>
        <button @click="applyFilters" class="secondary-button">Filtrar</button>
        <button
          v-if="createdFrom || createdTo"
          @click="clearDateFilter"
          class="system-button underline"
        >
          Limpiar
        </button>
      </div>
    </div>

    <div class="card mt-6">
      <table class="table-auto text-sm mt-2 bg-white">
        <thead class="text-left">
          <tr class="bg-primary-100">
            <th class="px-4 py-3 rounded-l-lg text-xs font-semibold text-primary-700 uppercase tracking-wide">ID</th>
            <th
              class="px-4 py-3 text-xs font-semibold text-primary-700 uppercase tracking-wide cursor-pointer select-none"
              @click="toggleSort('last_name')"
            >
              Nombre <span class="ml-1">{{ sortArrow('last_name') }}</span>
            </th>
            <th class="px-4 py-3 text-xs font-semibold text-primary-700 uppercase tracking-wide">Sucursal</th>
            <th
              class="px-4 py-3 text-xs font-semibold text-primary-700 uppercase tracking-wide cursor-pointer select-none"
              @click="toggleSort('created_at')"
            >
              Fecha de registro <span class="ml-1">{{ sortArrow('created_at') }}</span>
            </th>
            <th class="px-4 py-3 text-xs font-semibold text-primary-700 uppercase tracking-wide">Mascotas</th>
            <th class="px-4 py-3 rounded-r-lg text-xs font-semibold text-primary-700 uppercase tracking-wide">Acciones</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="client in clients" :key="client.id" class="table-row" :class="{'yellow-background': client.is_from_barfhero}">
            <td class="table-cell">
              {{ client.id }}
            </td>
            <td class="table-cell">{{ client.name }} {{ client.last_name }}</td>
            <td class="table-cell">{{ client.branch ? client.branch.name : "-" }}</td>
            <td class="table-cell whitespace-nowrap">{{ formatDate(client.created_at) }}</td>
            <td class="table-cell">
              <div class="flex flex-wrap">
                <div v-for="pet in client.pets" :key="pet.id" class="mr-4">
                  <router-link
                    :to="{
                      name: 'petsDetail',
                      params: { id: pet.id },
                    }"
                    class="underline system-button"
                    >{{ pet.name }}</router-link
                  >
                </div>
              </div>
            </td>
            <td class="table-cell">
              <div class="flex">
                <router-link
                  :to="{
                    name: 'clientsDetail',
                    params: { id: client.id },
                  }"
                  class="secondary-button"
                  >Detalle</router-link
                >
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <div v-if="loading" class="flex w-full h-full items-center">
        <loader-icon class="h-10 w-10 text-primary-500 mx-auto"></loader-icon>
      </div>
    </div>

    <pagination
      :current-page="currentPage"
      :total-pages="totalPages"
      @page-change="onPageChange"
    />
  </div>
</template>

<script>
import api from "@/api";
import Pagination from "@/components/ui/Pagination.vue";
export default {
  name: "Clients",

  components: {
    Pagination,
  },

  data() {
    return {
      clients: null,
      loading: false,
      searchText: "",
      currentPage: 1,
      totalPages: 0,
      sort: "created_at",
      direction: "desc",
      createdFrom: "",
      createdTo: "",
    };
  },

  created() {
    this.$store.commit("setSelectedTab", "clients");

    this.loadClients();
  },

  methods: {
    async loadClients() {
      this.loading = true;

      try {
        const response = await api.clients.getAllWithQueryParams([
          { name: "page", value: this.currentPage },
          { name: "search", value: this.searchText },
          { name: "sort", value: this.sort },
          { name: "direction", value: this.direction },
          { name: "created_from", value: this.createdFrom },
          { name: "created_to", value: this.createdTo },
        ]);
        this.clients = response.data;
        this.currentPage = response.current_page;
        this.totalPages = response.last_page;
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }

      this.loading = false;
    },

    search() {
      this.currentPage = 1;
      this.loadClients();
    },

    applyFilters() {
      this.currentPage = 1;
      this.loadClients();
    },

    clearDateFilter() {
      this.createdFrom = "";
      this.createdTo = "";
      this.currentPage = 1;
      this.loadClients();
    },

    toggleSort(column) {
      if (this.sort === column) {
        this.direction = this.direction === "asc" ? "desc" : "asc";
      } else {
        this.sort = column;
        this.direction = column === "created_at" ? "desc" : "asc";
      }
      this.currentPage = 1;
      this.loadClients();
    },

    sortArrow(column) {
      if (this.sort !== column) return "";
      return this.direction === "asc" ? "▲" : "▼";
    },

    formatDate(value) {
      if (!value) return "-";
      return new Date(value).toLocaleDateString("es-MX", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    },

    onPageChange(page) {
      this.currentPage = page;
      this.loadClients();
    },
  },
};
</script>

<style>
.yellow-background {
  background-color: #ffff00;
}
</style>
